<template>
  <v-container class="ma-auto wd-p-0 wd-pb-6" fluid>
    <client-only>
      <div v-if="isMobile" v-intersect="{ handler: onIntersect, options: { threshold: 0 } }" class="target-box" />
    </client-only>

    <category-info-sheet-mobile
      v-if="isMobile"
      :region="region"
      :has-geolocation="hasGeolocation"
      :filters="filterOptions"
      :total-count="totalCount"
      :is-fixed-top="isFixedTop"
      :from-page="FilterFromPage.TAG"
      :has-category-info="false"
      :is-branch-card-mode="isBranchCardMode"
      :tag-id="tagId"
      @update-condition="onConditionUpdate"
    >
      <template #title>
        <h1 class="wd-flex wd-items-center wd-px-5 wd-py-3 wd-text-otemplg wd-font-bold">
          <p class="!wd-mb-0">{{ region.name }}</p>
          <span class="wd-h-4 wd-mx-4 wd-border-solid wd-border-0 wd-border-r wd-border-gray50" />
          <p class="!wd-mb-0">{{ tag.name }}</p>
        </h1>
      </template>
    </category-info-sheet-mobile>
    <category-info-sheet
      v-else
      :region="region"
      :has-geolocation="hasGeolocation"
      :filters="filterOptions"
      :total-count="totalCount"
      :from-page="FilterFromPage.TAG"
      :has-category-info="false"
      :is-branch-card-mode="isBranchCardMode"
      :secondary-title="tag.name"
      :tag-id="tagId"
      @update-condition="onConditionUpdate"
    />

    <branch-product-grid
      :card-mode="aBTestingStore.cardMode"
      :branches="branchesInfo.branches"
      :products="categoryProductInfo.products"
      :is-loading="loadingList"
    >
      <template #footer>
        <pagination
          v-if="totalCount > 0"
          class="mb-9"
          :page="pagingState.page"
          :visible="8"
          :total-count="totalCount"
          :size="pagingState.size"
          @update="updatePagingPage"
        />
      </template>
    </branch-product-grid>
  </v-container>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { useBreakpoints } from '@/composition/useBreakpoints'
import { useRegionSearchNavigation } from '@/composables/useRegionSearchNavigation'
import { useRouteQueryFilterSync } from '@/composables/useRouteQueryFilterSync'

import CategoryInfoSheet from '@/components/page/categories/category-info-sheet.vue'
import CategoryInfoSheetMobile from '@/components/page/categories/mobile/category-info-sheet--mobile.vue'
import BranchProductGrid from '@/components/page/list-page/BranchProductGrid.vue'

import { useTagStore } from '@/store-pinia/tag'
import { useABTestingStore } from '@/store-pinia/abtesting'

import { getCategoryDefaultFiltersFromQuery } from '@/utils/filter'
import { FilterFromPage } from '@/constants/filters'
import { ResponseCode } from '@/view-models/common'
import utils from '@/utils'

const Pagination = defineAsyncComponent(() => import('@/components/pagination/pagination.vue'))

const nuxtApp = useNuxtApp()
const { $store: store, $pinia } = nuxtApp
const route = useRoute()
const i18n = useI18n()
const config = useRuntimeConfig()

const tagStore = useTagStore()
const { tag } = storeToRefs(tagStore)
const aBTestingStore = useABTestingStore($pinia)

const { isMobile } = useBreakpoints()
const { pagingState, userPosition, hasGeolocation } = useRegionSearchNavigation({
  fetchList: getList,
})

const loadingList = ref(false)
const isFixedTop = ref(false)

const regionId = computed(() => Number(route.params.rid))
const tagId = computed(() => Number(route.params.id))

const region = computed(() => store.getters['region/region'])
const branchesInfo = computed(() => store.getters['branch/branchesInfo'])
const categoryProductInfo = computed(() => store.getters['product/categoryProductInfo'])
const selectedFilters = computed(() => store.getters['filter/filters'])
const sortType = computed(() => store.getters['filter/sortType'])

const isBranchCardMode = computed(() => aBTestingStore.cardMode === 'branch')
const totalCount = computed(() => (isBranchCardMode.value ? branchesInfo.value.totalCount : categoryProductInfo.value.totalCount) || 0)

useHead(() => {
  const tagTitle = tag.value?.name ?? ''
  const titleSuffix = i18n.t('funnow_title')
  const pageTitle = tagTitle ? `${tagTitle} | ${titleSuffix}` : titleSuffix
  const canonicalUrl = `${config.public.BASE_URL}${route.path}`
  const ogImage = utils.ogImageUrl(tag.value?.cover_image)

  return {
    title: pageTitle,
    meta: [
      { hid: 'title', name: 'title', content: pageTitle },
      { hid: 'og:title', property: 'og:title', content: pageTitle },
      { hid: 'og:image', property: 'og:image', content: ogImage },
    ],
    link: [{ hid: 'i18n-can', rel: 'canonical', href: canonicalUrl }],
  }
})

const { data: filterOptionsData } = await useAsyncData(async () => {
  try {
    if (!tagId.value) {
      throw new Error(`Invalid tag id: ${route.params.id}`)
    }

    const [, , filtersOptionsRes] = await Promise.all([
      store.dispatch('region/getRegion', regionId.value),
      tagStore.fetchTag(tagId.value),
      fetchFilterOptions({ regionID: regionId.value }),
    ])
    const filtersFromQuery = getCategoryDefaultFiltersFromQuery(
      route.query as Record<string, string | unknown[]>,
      filtersOptionsRes,
      hasGeolocation.value
    )
    store.dispatch('filter/resetAndSetFilters', filtersFromQuery)

    await getList()

    return filtersOptionsRes
  } catch (err) {
    console.error('useAsyncData error', err)
    throw showError({ statusCode: 404, message: 'Tag not found', fatal: true })
  }
})

const filterOptions = computed(() => filterOptionsData.value ?? {})

useRouteQueryFilterSync({
  filterOptions,
  hasGeolocation,
  selectedFilters,
  pagingState,
  updateSelectedFilters,
  getProductList: getList,
})

onUnmounted(() => {
  tagStore.resetTag()
})

function updateSelectedFilters(newPartialFilters: unknown) {
  store.dispatch('filter/setFilters', newPartialFilters)
}

function buildListPayload() {
  const startIndex = (pagingState.page - 1) * pagingState.size
  const payload: Record<string, unknown> = {
    sortby: sortType.value,
    filter_regionid: regionId.value,
    filter_tag: `${tagId.value}`,
    size: pagingState.size,
    start: startIndex,
  }

  if (hasGeolocation.value) {
    payload.lng = userPosition.lng
    payload.lat = userPosition.lat
  }

  return payload
}

async function getList() {
  const basePayload = buildListPayload()

  try {
    loadingList.value = true

    const { page, ...payload } = { ...basePayload, ...selectedFilters.value }
    const dispatchAction = isBranchCardMode.value ? 'branch/getBranchList' : 'product/getProductList'

    await store.dispatch(dispatchAction, payload)
  } catch (error) {
    console.error('getList error', error)
  } finally {
    loadingList.value = false
  }
}

async function fetchFilterOptions(params: { regionID: number }) {
  try {
    const res = await store.dispatch('filter/getFilters', { regionID: params.regionID })

    return res?.code === ResponseCode.Success ? res.data : {}
  } catch (error) {
    console.error('fetchFilterOptions error', error)
    return {}
  }
}

function onConditionUpdate({ filter }: { sort: string; filter: Record<string, unknown> }) {
  const firstPage = 1
  const currentPage = pagingState.page
  const newQuery = { ...filter, page: firstPage }

  updateSelectedFilters(newQuery)
  updatePagingPage(firstPage)
  navigateTo({ query: newQuery })

  if (currentPage === firstPage) {
    getList()
  }
}

function updatePagingPage(newPage: number) {
  pagingState.page = newPage
}

function onIntersect(isIntersecting: boolean) {
  isFixedTop.value = !isIntersecting
}
</script>
